import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type {
	DataSecurityPolicyAction,
	useIsAttachmentDownloadDisabledQuery$data,
	useIsAttachmentDownloadDisabledQuery$variables,
	DataSecurityPolicyDecidableContentStatus,
} from './__generated__/useIsAttachmentDownloadDisabledQuery.graphql.ts';

interface UseIsAttachmentDownloadDisabledArgs {
	contentId: string;
	spaceKey?: string | null;
	skip?: boolean;
}

const useIsAttachmentDownloadDisabledQuery = graphql`
	query useIsAttachmentDownloadDisabledQuery(
		$action: DataSecurityPolicyAction!
		$contentId: ID!
		$contentStatus: DataSecurityPolicyDecidableContentStatus!
		$contentVersion: Int!
		$spaceKey: String
	) {
		dataSecurityPolicy {
			isActionEnabledForContent(
				action: $action
				contentId: $contentId
				contentStatus: $contentStatus
				contentVersion: $contentVersion
				spaceKey: $spaceKey
			) {
				action
				allowed
			}
		}
	}
`;

export const getAttachmentDSPQueryVariables = (
	action: DataSecurityPolicyAction,
	contentId: string,
	spaceKey?: string | null,
): {
	action: DataSecurityPolicyAction;
	contentId: string;
	contentStatus: DataSecurityPolicyDecidableContentStatus;
	contentVersion: number;
	spaceKey: string | null | undefined;
} => ({
	action,
	contentId,
	contentStatus: 'CURRENT' as DataSecurityPolicyDecidableContentStatus,
	contentVersion: 1,
	spaceKey,
});

export const useIsAttachmentDownloadDisabled = ({
	contentId,
	spaceKey,
	skip,
}: UseIsAttachmentDownloadDisabledArgs): {
	isDisabled: boolean;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
} => {
	const action: DataSecurityPolicyAction = 'ATTACHMENT_DOWNLOAD';

	const { data, loading, error } = useQueryInterop<
		useIsAttachmentDownloadDisabledQuery$data,
		useIsAttachmentDownloadDisabledQuery$variables
	>(useIsAttachmentDownloadDisabledQuery, {
		variables: getAttachmentDSPQueryVariables(action, contentId, spaceKey),
		context: {
			allowOnExternalPage: true,
		},
		skip,
	});
	const allowedResp = data?.dataSecurityPolicy?.isActionEnabledForContent?.allowed;
	// Defaults to disabled if we don't have a definitive answer
	const isDisabled = allowedResp === undefined || allowedResp === null ? true : !allowedResp;
	return {
		isDisabled,
		loading,
		error,
	};
};
